import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { getAuthHeaders } from '../utils/auth';
import './Modal.css';

function SettingsModal({ settings, onClose, onSave, onLogout }) {
  const [formData, setFormData] = useState({
    ...settings,
    weatherLocation: settings.weatherLocation || 'New York, NY',
    temperatureUnit: settings.temperatureUnit || 'F'
  });
  const [rssFeeds, setRssFeeds] = useState([]);
  const [newFeedUrl, setNewFeedUrl] = useState('');
  const [newFeedName, setNewFeedName] = useState('');
  const [activeTab, setActiveTab] = useState('appearance');

  useEffect(() => {
    loadRssFeeds();
  }, []);

  const loadRssFeeds = async () => {
    try {
      const response = await axios.get('/api/rss/feeds', {
        headers: getAuthHeaders()
      });
      setRssFeeds(response.data);
    } catch (error) {
      console.error('Error loading RSS feeds:', error);
    }
  };

  const handleAddRssFeed = async (e) => {
    e.preventDefault();
    if (!newFeedUrl.trim()) return;

    try {
      await axios.post('/api/rss/feeds', {
        url: newFeedUrl,
        name: newFeedName || newFeedUrl
      }, {
        headers: getAuthHeaders()
      });
      setNewFeedUrl('');
      setNewFeedName('');
      loadRssFeeds();
    } catch (error) {
      console.error('Error adding RSS feed:', error);
      alert('Failed to add RSS feed');
    }
  };

  const handleDeleteRssFeed = async (id) => {
    if (!window.confirm('Are you sure you want to delete this RSS feed?')) {
      return;
    }
    try {
      await axios.delete(`/api/rss/feeds/${id}`, {
        headers: getAuthHeaders()
      });
      loadRssFeeds();
    } catch (error) {
      console.error('Error deleting RSS feed:', error);
      alert('Failed to delete RSS feed');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
    onClose();
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const presetThemes = [
    { name: 'Default', backgroundColor: '#f5f7fa', cardColor: '#ffffff', textColor: '#333333', accentColor: '#667eea' },
    { name: 'Dark', backgroundColor: '#1a1a1a', cardColor: '#2d2d2d', textColor: '#ffffff', accentColor: '#667eea' },
    { name: 'Ocean', backgroundColor: '#e0f2fe', cardColor: '#ffffff', textColor: '#0c4a6e', accentColor: '#0284c7' },
    { name: 'Forest', backgroundColor: '#f0fdf4', cardColor: '#ffffff', textColor: '#14532d', accentColor: '#16a34a' },
    { name: 'Sunset', backgroundColor: '#fff7ed', cardColor: '#ffffff', textColor: '#7c2d12', accentColor: '#ea580c' }
  ];

  const applyPreset = (preset) => {
    setFormData({
      ...formData,
      backgroundColor: preset.backgroundColor,
      cardColor: preset.cardColor,
      textColor: preset.textColor,
      accentColor: preset.accentColor
    });
  };

  const modalContent = (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content settings-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Settings</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="settings-tabs">
          <button
            type="button"
            className={`settings-tab ${activeTab === 'appearance' ? 'active' : ''}`}
            onClick={() => setActiveTab('appearance')}
          >
            Appearance
          </button>
          <button
            type="button"
            className={`settings-tab ${activeTab === 'weather' ? 'active' : ''}`}
            onClick={() => setActiveTab('weather')}
          >
            Weather
          </button>
          <button
            type="button"
            className={`settings-tab ${activeTab === 'rss' ? 'active' : ''}`}
            onClick={() => setActiveTab('rss')}
          >
            RSS Feeds
          </button>
        </div>

        {activeTab === 'appearance' && (
        <form onSubmit={handleSubmit}>
          <div className="settings-section">
            <h3>Preset Themes</h3>
            <div className="preset-themes">
              {presetThemes.map((preset, index) => (
                <button
                  key={index}
                  type="button"
                  className="preset-button"
                  onClick={() => applyPreset(preset)}
                  style={{
                    backgroundColor: preset.backgroundColor,
                    borderColor: preset.accentColor
                  }}
                >
                  {preset.name}
                </button>
              ))}
            </div>
          </div>

          <div className="settings-section">
            <h3>Custom Colors</h3>
            <div className="form-group">
              <label htmlFor="backgroundColor">Background Color</label>
              <div className="color-input-group">
                <input
                  type="color"
                  id="backgroundColor"
                  name="backgroundColor"
                  value={formData.backgroundColor}
                  onChange={handleChange}
                />
                <input
                  type="text"
                  value={formData.backgroundColor}
                  onChange={handleChange}
                  name="backgroundColor"
                />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="cardColor">Card Color</label>
              <div className="color-input-group">
                <input
                  type="color"
                  id="cardColor"
                  name="cardColor"
                  value={formData.cardColor}
                  onChange={handleChange}
                />
                <input
                  type="text"
                  value={formData.cardColor}
                  onChange={handleChange}
                  name="cardColor"
                />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="textColor">Text Color</label>
              <div className="color-input-group">
                <input
                  type="color"
                  id="textColor"
                  name="textColor"
                  value={formData.textColor}
                  onChange={handleChange}
                />
                <input
                  type="text"
                  value={formData.textColor}
                  onChange={handleChange}
                  name="textColor"
                />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="accentColor">Accent Color</label>
              <div className="color-input-group">
                <input
                  type="color"
                  id="accentColor"
                  name="accentColor"
                  value={formData.accentColor}
                  onChange={handleChange}
                />
                <input
                  type="text"
                  value={formData.accentColor}
                  onChange={handleChange}
                  name="accentColor"
                />
              </div>
            </div>
          </div>

          <div className="settings-section">
            <h3>Background Image</h3>
            <div className="form-group">
              <label htmlFor="backgroundImage">Background Image URL (optional)</label>
              <input
                type="text"
                id="backgroundImage"
                name="backgroundImage"
                value={formData.backgroundImage}
                onChange={handleChange}
                placeholder="https://example.com/image.jpg"
              />
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="cancel-button">
              Cancel
            </button>
            <button type="submit" className="save-button">
              Save Settings
            </button>
          </div>
        </form>
        )}

        {activeTab === 'weather' && (
          <div className="weather-settings-section">
            <div className="settings-section">
              <h3>Weather Settings</h3>
              <div className="form-group">
                <label htmlFor="weatherLocation">Location</label>
                <input
                  type="text"
                  id="weatherLocation"
                  name="weatherLocation"
                  value={formData.weatherLocation || ''}
                  onChange={handleChange}
                  placeholder="New York, NY"
                />
                <p style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.5)', marginTop: '4px' }}>
                  Enter city name (e.g., "New York, NY" or "London, UK")
                </p>
              </div>
              <div className="form-group">
                <label htmlFor="temperatureUnit">Temperature Unit</label>
                <select
                  id="temperatureUnit"
                  name="temperatureUnit"
                  value={formData.temperatureUnit || 'F'}
                  onChange={handleChange}
                  style={{
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    background: 'rgba(255, 255, 255, 0.05)',
                    color: '#ffffff',
                    fontSize: '14px',
                    width: '100%'
                  }}
                >
                  <option value="F">Fahrenheit (°F)</option>
                  <option value="C">Celsius (°C)</option>
                </select>
              </div>
              <div className="modal-actions" style={{ marginTop: '20px' }}>
                <button type="button" onClick={onClose} className="cancel-button">
                  Cancel
                </button>
                <button type="button" onClick={handleSubmit} className="save-button">
                  Save Settings
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'rss' && (
          <div className="rss-feeds-section">
            <div className="settings-section">
              <h3>Add RSS Feed</h3>
              <form onSubmit={handleAddRssFeed} style={{ marginBottom: '20px' }}>
                <div className="form-group">
                  <label htmlFor="rssUrl">RSS Feed URL *</label>
                  <input
                    type="url"
                    id="rssUrl"
                    value={newFeedUrl}
                    onChange={(e) => setNewFeedUrl(e.target.value)}
                    placeholder="https://example.com/feed.xml"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="rssName">Feed Name (optional)</label>
                  <input
                    type="text"
                    id="rssName"
                    value={newFeedName}
                    onChange={(e) => setNewFeedName(e.target.value)}
                    placeholder="My News Feed"
                  />
                </div>
                <button type="submit" className="save-button" style={{ width: '100%' }}>
                  Add RSS Feed
                </button>
              </form>
            </div>

            <div className="settings-section">
              <h3>Manage RSS Feeds</h3>
              {rssFeeds.length === 0 ? (
                <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '14px' }}>
                  No RSS feeds added yet.
                </p>
              ) : (
                <div className="rss-feeds-list">
                  {rssFeeds.map(feed => (
                    <div key={feed.id} className="rss-feed-item">
                      <div className="rss-feed-info">
                        <div className="rss-feed-name">{feed.name}</div>
                        <div className="rss-feed-url">{feed.url}</div>
                      </div>
                      <button
                        type="button"
                        className="url-action-button"
                        onClick={() => handleDeleteRssFeed(feed.id)}
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="settings-section" style={{ marginTop: '30px', borderTop: '1px solid rgba(255, 255, 255, 0.1)', paddingTop: '20px' }}>
          <button 
            type="button" 
            className="logout-button-modal" 
            onClick={() => {
              onClose();
              onLogout();
            }}
          >
            Logout
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}

export default SettingsModal;

